import { useState } from 'react';
import { Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import { invitesAPI, settingsAPI } from '../../lib/api';
import { PHONE_APP_COPY, emailLooksCaptured, phoneOfferMode, usePhoneAppOffer } from '../../lib/phoneAppOffer';
import SettingsSectionCard from '../settings/SettingsSectionCard';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../ui/dialog';

/**
 * Shared phone app offers.
 * The two actions sit side by side with a gap so they never touch.
 * A join stays unconfirmed until an email is already captured.
 * Once confirmed, the label says they are on that waitlist.
 * IOS_APP_STORE_URL swaps only the Apple option.
 * ANDROID_PLAY_STORE_URL swaps only the Android option.
 */
const FULL_ACTION = 'flex h-full min-h-12 w-full min-w-0 items-center justify-center rounded-2xl px-3 py-3 text-center text-sm font-semibold leading-snug';

function StoreLink({ href, label, testId }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={`${FULL_ACTION} bg-nav-blue text-white hover:bg-nav-blue/90`}
      data-testid={testId}
    >
      {label}
    </a>
  );
}

function WaitlistEmailDialog({
  platform, initialEmail, updateUrl, onClose, onJoined, accountOwned = false,
}) {
  const [email, setEmail] = useState(initialEmail || '');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [hasApp, setHasApp] = useState(null);
  const confirm = async () => {
    if (!emailLooksCaptured(email)) {
      setNotice(PHONE_APP_COPY.emailNeeded);
      setHasApp(null);
      return;
    }
    setBusy(true);
    setNotice('');
    try {
      const { data } = await invitesAPI.betaRequest({
        email: email.trim().toLowerCase(),
        device: platform === 'android' ? 'android' : 'ios',
        source: 'signup',
      });
      if (accountOwned && data?.already_has_app) {
        setHasApp(data.update_url || updateUrl || '');
        setNotice(PHONE_APP_COPY.alreadyHasApp);
        return;
      }
      onJoined?.();
      onClose();
    } catch {
      setNotice('Could not join the waitlist. Try again.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent data-testid="waitlist-email-dialog">
        <DialogTitle>{PHONE_APP_COPY.joinTitle}</DialogTitle>
        <DialogDescription>
          {platform === 'android' ? PHONE_APP_COPY.androidLabel : PHONE_APP_COPY.iosLabel}
        </DialogDescription>
        <Input
          type="email"
          autoFocus
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder="Email"
          data-testid="waitlist-email-input"
        />
        {notice ? (
          <p className="text-sm text-foreground" data-testid="waitlist-email-notice">{notice}</p>
        ) : null}
        {hasApp ? (
          <a href={hasApp} className="text-sm font-semibold text-nav-blue" data-testid="waitlist-update-link">
            {PHONE_APP_COPY.updateLink}
          </a>
        ) : null}
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={onClose} className="flex-1">Close</Button>
          <Button type="button" onClick={confirm} disabled={busy} className="flex-1 bg-nav-blue text-white" data-testid="waitlist-email-confirm">
            {busy ? 'Joining...' : PHONE_APP_COPY.joinConfirm}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function WaitlistBox({
  testId, joinLabel, joinedLabel, checked, confirmed, onChange, disabled, describedBy,
}) {
  const label = confirmed ? joinedLabel : joinLabel;
  const tone = confirmed
    ? 'bg-nav-blue text-white hover:bg-nav-blue/90'
    : checked
      ? 'border-2 border-nav-blue bg-nav-blue/10 text-nav-blue'
      : 'border-2 border-nav-blue bg-white text-nav-blue hover:bg-nav-blue/5';
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked || confirmed}
      aria-describedby={describedBy}
      aria-label={label}
      disabled={disabled}
      data-testid={testId}
      onClick={() => onChange?.(!checked)}
      className={`${FULL_ACTION} ${tone} disabled:opacity-60`}
    >
      {label}
    </button>
  );
}

export default function PhoneAppOffers({
  variant = 'signup',
  ready = false,
  appStoreUrl = null,
  playStoreUrl = null,
  iosJoined = false,
  androidJoined = false,
  iosConfirmed,
  androidConfirmed,
  onIosChange,
  onAndroidChange,
  iosDisabled = false,
  androidDisabled = false,
  emailDialog = false,
  formEmail = '',
  accountOwned = false,
}) {
  const [dialogPlatform, setDialogPlatform] = useState(null);
  if (!ready) return null;
  const ios = phoneOfferMode(appStoreUrl);
  const android = phoneOfferMode(playStoreUrl);
  const showHelper = variant !== 'login' && variant !== 'signup' && (ios.mode === 'waitlist' || android.mode === 'waitlist');
  const helperId = variant === 'settings'
    ? 'settings-phone-waitlist-helper'
    : variant === 'import'
      ? 'import-phone-waitlist-helper'
      : 'signup-phone-waitlist-helper';
  const describedBy = showHelper ? helperId : undefined;
  const iosControl = ios.mode === 'store'
    ? <StoreLink href={ios.url} label={PHONE_APP_COPY.appStoreLabel} testId={`${variant}-ios-app-store-link`} />
    : (
      <WaitlistBox
        testId={`${variant}-ios-waitlist`}
        joinLabel={PHONE_APP_COPY.iosLabel}
        joinedLabel={PHONE_APP_COPY.iosJoinedLabel}
        checked={iosJoined}
        confirmed={iosConfirmed ?? iosJoined}
        onChange={emailDialog ? () => { if (!iosJoined) setDialogPlatform('ios'); } : onIosChange}
        disabled={iosDisabled}
        describedBy={describedBy}
      />
    );
  const androidControl = android.mode === 'store'
    ? <StoreLink href={android.url} label={PHONE_APP_COPY.playStoreLabel} testId={`${variant}-android-play-store-link`} />
    : (
      <WaitlistBox
        testId={`${variant}-android-waitlist`}
        joinLabel={PHONE_APP_COPY.androidLabel}
        joinedLabel={PHONE_APP_COPY.androidJoinedLabel}
        checked={androidJoined}
        confirmed={androidConfirmed ?? androidJoined}
        onChange={emailDialog ? () => { if (!androidJoined) setDialogPlatform('android'); } : onAndroidChange}
        disabled={androidDisabled}
        describedBy={describedBy}
      />
    );
  const helper = showHelper ? (
    <p id={helperId} className="text-xs text-muted-foreground leading-snug" data-testid={helperId}>
      {PHONE_APP_COPY.helper}
    </p>
  ) : null;
  const pitch = variant === 'signup' || variant === 'welcome' || variant === 'invite' ? (
    <div className="flex flex-col gap-1" data-testid={`${variant}-phone-apps-pitch`}>
      <p className="text-sm font-medium text-foreground">{PHONE_APP_COPY.comingSoon}</p>
    </div>
  ) : null;
  const body = (
    <div className="flex flex-col gap-3" data-testid={`${variant}-phone-offers`}>
      {dialogPlatform ? (
        <WaitlistEmailDialog
          platform={dialogPlatform}
          initialEmail={formEmail}
          updateUrl={dialogPlatform === 'android' ? playStoreUrl : appStoreUrl}
          accountOwned={accountOwned}
          onClose={() => setDialogPlatform(null)}
          onJoined={() => (dialogPlatform === 'android' ? onAndroidChange?.(true) : onIosChange?.(true))}
        />
      ) : null}
      {pitch}
      <div className="grid grid-cols-2 gap-3 items-stretch" data-testid={`${variant}-phone-offer-row`}>
        {iosControl}
        {androidControl}
      </div>
      {helper}
    </div>
  );
  if (variant === 'settings') {
    return (
      <SettingsSectionCard icon={Smartphone} title={PHONE_APP_COPY.settingsTitle}>
        {body}
      </SettingsSectionCard>
    );
  }
  return body;
}

function usePlatformToggle({ joined, write, refreshUser, failure, email }) {
  const [optimistic, setOptimistic] = useState(null);
  const [pending, setPending] = useState(false);
  const value = optimistic === null ? joined : optimistic;
  const onChange = async (next) => {
    if (next && !emailLooksCaptured(email)) {
      toast.error(PHONE_APP_COPY.emailNeeded);
      return;
    }
    setOptimistic(next);
    setPending(true);
    try {
      await write(next);
      try {
        await refreshUser?.();
      } catch {
        // The write already landed. The next settings load can refresh.
      }
      setOptimistic(null);
    } catch (err) {
      setOptimistic(null);
      toast.error(err?.response?.data?.detail || failure);
    } finally {
      setPending(false);
    }
  };
  return { value, pending, onChange };
}

export function SettingsPhoneWaitlists({ user, refreshUser, variant = 'settings' }) {
  const { ready, appStoreUrl, playStoreUrl } = usePhoneAppOffer();
  const ios = usePlatformToggle({
    joined: user?.ios_waitlist === true,
    write: settingsAPI.setIosWaitlist,
    refreshUser,
    failure: 'Could not update the Apple app waitlist',
    email: user?.email,
  });
  const android = usePlatformToggle({
    joined: user?.android_waitlist === true,
    write: settingsAPI.setAndroidWaitlist,
    refreshUser,
    failure: 'Could not update the Android app waitlist',
    email: user?.email,
  });
  return (
    <PhoneAppOffers
      variant={variant}
      ready={ready}
      appStoreUrl={appStoreUrl}
      playStoreUrl={playStoreUrl}
      iosJoined={ios.value}
      androidJoined={android.value}
      onIosChange={ios.onChange}
      onAndroidChange={android.onChange}
      iosDisabled={ios.pending}
      androidDisabled={android.pending}
    />
  );
}
